/**
 * /admin/marketing/enrol-leads — Session 88 pt6.
 *
 * Single screen with two tabs, deep-linked from the daily marketing
 * health-check email:
 *
 *   • "Un-enrolled leads" (default) — active leads with no active
 *     workflow enrolment. Multi-select rows, pick a campaign from
 *     the dropdown (defaulted to "Inbound Response To Sales Leads"),
 *     hit "Bulk enrol" — re-uses the per-lead enrol path so contact
 *     upsert + idempotency stay identical to the per-lead button.
 *
 *   • "Orphan enrolments" — enrolment rows pointing at workflows
 *     whose status is no longer 'active'. Multi-select and stop them.
 *
 * The orphan tab is reached via `?tab=orphans` from the daily alert
 * email's "View orphan entries →" CTA.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import AppLayout from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const BULK_CAP = 50;

export default function EnrolLeadsPage() {
  const [params, setParams] = useSearchParams();
  const initialTab = params.get("tab") === "orphans" ? "orphans" : "unenrolled";
  const [tab, setTab] = useState(initialTab);

  return (
    <AppLayout>
      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-6">
          <div className="text-xs uppercase tracking-widest text-neutral-500">
            Admin · Marketing
          </div>
          <h1 className="text-3xl font-semibold mt-1 text-neutral-900">
            Enrol leads &amp; clean orphans
          </h1>
          <p className="text-sm text-neutral-600 mt-2 max-w-2xl">
            Surface leads that have no active workflow enrolment and put
            them in a drip campaign in one click. The &ldquo;Orphan
            enrolments&rdquo; tab lists rows whose workflow is no longer
            active &mdash; stop them so they don&apos;t accumulate.
          </p>
        </div>

        <div className="flex gap-1 border-b border-neutral-200 mb-6">
          <TabButton
            active={tab === "unenrolled"}
            onClick={() => { setTab("unenrolled"); setParams({}); }}
            label="Un-enrolled leads"
            testid="tab-unenrolled"
          />
          <TabButton
            active={tab === "orphans"}
            onClick={() => { setTab("orphans"); setParams({ tab: "orphans" }); }}
            label="Orphan enrolments"
            testid="tab-orphans"
          />
        </div>

        {tab === "unenrolled" ? <UnenrolledLeadsTab /> : <OrphanEnrolmentsTab />}
      </div>
    </AppLayout>
  );
}


function TabButton({ active, onClick, label, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={
        "px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors " +
        (active
          ? "border-neutral-900 text-neutral-900"
          : "border-transparent text-neutral-500 hover:text-neutral-800")
      }
    >
      {label}
    </button>
  );
}


// ─── Un-enrolled leads tab ──────────────────────────────────────────


function UnenrolledLeadsTab() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [workflows, setWorkflows] = useState([]);
  const [workflowId, setWorkflowId] = useState("");
  const [enroling, setEnroling] = useState(false);
  // Scope keeps deliberately-excluded populations VISIBLE without ever
  // mixing them into the enrolment queue. Only `actionable` may be enrolled.
  const [scope, setScope] = useState("actionable");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = async (nextScope) => {
    const sc = nextScope || scope;
    setLoading(true);
    setSelected(new Set());
    try {
      const [lr, wr] = await Promise.all([
        api.get(`/admin/marketing/unenrolled-leads?limit=500&scope=${sc}`),
        api.get("/admin/marketing/enrolment-workflows"),
      ]);
      setLeads(lr.data.items || []);
      setWorkflows(wr.data.items || []);
      setWorkflowId(wr.data.default_workflow_id || (wr.data.items?.[0]?.id ?? ""));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter(l =>
      (l.contact_email || "").toLowerCase().includes(q) ||
      (l.contact_person || "").toLowerCase().includes(q) ||
      (l.company_name || "").toLowerCase().includes(q)
    );
  }, [leads, search]);

  const toggle = (id) => {
    setSelected(s => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(l => l.lead_id)));
  };

  // ─── Bulk cleanup ────────────────────────────────────────────────
  // Three deliberately distinct actions. "Delete" ARCHIVES: crm_leads has
  // no foreign keys, so removing rows would silently orphan quotes,
  // projects, notes, events and the send history. Deleting a bad lead
  // does NOT suppress the address — that is the separate action.
  const ACTIONS = {
    delete: {
      label: "Delete selected",
      endpoint: "/admin/marketing/leads/bulk-delete",
      title: (n) => `Delete ${n} selected lead${n === 1 ? "" : "s"}?`,
      lines: (n) => [
        `${n} lead${n === 1 ? "" : "s"} will be removed from active CRM and Marketing use.`,
        "Any current Marketing enrolments will be cancelled.",
        "Existing email and audit history will be retained.",
        "The email address will NOT be suppressed.",
      ],
      danger: true,
    },
    deleteSuppress: {
      label: "Delete + suppress",
      endpoint: "/admin/marketing/leads/bulk-delete?suppress=true",
      title: (n) => `Delete and suppress ${n} selected lead${n === 1 ? "" : "s"}?`,
      lines: (n) => [
        `${n} lead${n === 1 ? "" : "s"} will be removed from active CRM and Marketing use.`,
        "Any current Marketing enrolments will be cancelled.",
        "Existing email and audit history will be retained.",
        "Future Marketing email to these addresses will be blocked.",
      ],
      danger: true,
    },
    cancel: {
      label: "Cancel enrolment",
      endpoint: "/admin/marketing/leads/bulk-cancel-enrolment",
      title: (n) => `Cancel Marketing enrolment for ${n} lead${n === 1 ? "" : "s"}?`,
      lines: (n) => [
        "The current Marketing sequence will stop.",
        "The CRM lead records stay in active use.",
        "No lead is archived and no address is suppressed.",
      ],
      danger: false,
    },
  };

  const askConfirm = async (key) => {
    if (selected.size === 0) { toast.warning("Select at least one lead"); return; }
    const ids = [...selected];
    let related = null;
    try {
      const r = await api.post("/admin/marketing/leads/bulk-preview", { lead_ids: ids });
      related = r.data?.related || null;
    } catch { /* preview is advisory only */ }
    setConfirm({ key, ids, related });
  };

  const runAction = async () => {
    if (!confirm) return;
    const { key, ids } = confirm;
    const act = ACTIONS[key];
    setBusy(true);
    try {
      const r = await api.post(act.endpoint, { lead_ids: ids, reason: "operator bulk cleanup" });
      const d = r.data || {};
      toast.success(
        key === "cancel"
          ? `Cancelled ${d.cancelled} enrolment${d.cancelled === 1 ? "" : "s"}`
          : `Archived ${d.archived} · ${d.cancelled} enrolment${d.cancelled === 1 ? "" : "s"} cancelled` +
            (d.suppressed ? ` · ${d.suppressed} suppressed` : "")
      );
      setConfirm(null);
      setSelected(new Set());
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk action failed");
    } finally {
      setBusy(false);
    }
  };

  const bulkEnrol = async () => {
    if (selected.size === 0) {
      toast.warning("Select at least one lead");
      return;
    }
    if (!workflowId) {
      toast.warning("Pick a workflow");
      return;
    }
    const ids = [...selected];
    const big = ids.length > BULK_CAP;
    if (big) {
      const wfName = workflows.find(w => w.id === workflowId)?.name || workflowId;
      const ok = window.confirm(
        `You're about to enrol ${ids.length} leads into "${wfName}" — ` +
        `more than the ${BULK_CAP}-lead cap. Continue?`
      );
      if (!ok) return;
    }
    setEnroling(true);
    try {
      const r = await api.post("/admin/marketing/bulk-enrol", {
        lead_ids: ids, workflow_id: workflowId,
        confirm_large_batch: big,
      });
      const d = r.data;
      toast.success(
        `Enrolled ${d.enrolled} · skipped ${d.skipped} · failed ${d.failed}` +
        ` → "${d.workflow_name}"`
      );
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk enrol failed");
    } finally {
      setEnroling(false);
    }
  };

  if (loading) return <div className="text-neutral-500 text-sm">Loading…</div>;

  return (
    <div data-testid="unenrolled-pane">
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="flex-1 min-w-[240px]">
          <label className="text-xs uppercase tracking-wider text-neutral-500">
            Search leads
          </label>
          <Input
            data-testid="lead-search"
            placeholder="email, name, or company…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="min-w-[300px]">
          <label className="text-xs uppercase tracking-wider text-neutral-500">
            Enrol into workflow
          </label>
          <Select value={workflowId} onValueChange={setWorkflowId}>
            <SelectTrigger data-testid="workflow-picker">
              <SelectValue placeholder="Pick a workflow" />
            </SelectTrigger>
            <SelectContent>
              {workflows.map(w => (
                <SelectItem key={w.id} value={w.id}>
                  {w.is_default ? "★ " : ""}{w.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          data-testid="bulk-enrol-btn"
          onClick={bulkEnrol}
          disabled={enroling || selected.size === 0 || scope !== "actionable"}
          title={scope !== "actionable"
            ? "Only leads in the 'Ready for enrolment' view can be enrolled"
            : undefined}
          className="bg-neutral-900 hover:bg-neutral-800"
        >
          {enroling
            ? "Enroling…"
            : `Enrol ${selected.size || ""} ${selected.size === 1 ? "lead" : "leads"}`.trim()}
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        {[
          ["actionable", "Ready for enrolment"],
          ["held", "Held"],
          ["suppressed", "Suppressed / ineligible"],
          ["archived", "Archived / deleted"],
          ["all", "All (historical)"],
        ].map(([key, label]) => (
          <button
            key={key}
            data-testid={`scope-${key}`}
            onClick={() => { setScope(key); load(key); }}
            className={
              "px-3 py-1 rounded-full text-xs border " +
              (scope === key
                ? "bg-neutral-900 text-white border-neutral-900"
                : "bg-white text-neutral-600 border-neutral-300 hover:bg-neutral-50")
            }
          >
            {label}
          </button>
        ))}
      </div>

      {scope !== "actionable" && (
        <div
          data-testid="scope-warning"
          className="mb-3 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
        >
          These leads are deliberately excluded from Marketing — held,
          suppressed or archived. They are shown so you can review or clean
          them up. They are <strong>not</strong> enrolment candidates.
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 text-xs text-neutral-500 mb-2">
        <span>
          {filtered.length} of {leads.length}{" "}
          {scope === "actionable" ? "leads ready for enrolment" : "leads in this view"}
        </span>
        {selected.size > 0 && (
          <>
            <span data-testid="selected-count" className="font-semibold text-neutral-900">
              {selected.size} selected
            </span>
            <button
              data-testid="select-all-matching"
              onClick={() => setSelected(new Set(filtered.map((l) => l.lead_id)))}
              className="underline hover:text-neutral-900"
            >
              Select all {filtered.length} matching
            </button>
            <button
              data-testid="clear-selection"
              onClick={() => setSelected(new Set())}
              className="underline hover:text-neutral-900"
            >
              Clear selection
            </button>
          </>
        )}
      </div>

      {selected.size > 0 && (
        <div
          data-testid="bulk-action-bar"
          className="flex flex-wrap items-center gap-2 mb-3 rounded border border-neutral-200 bg-neutral-50 px-3 py-2"
        >
          <span className="text-xs text-neutral-600 mr-1">
            {selected.size} selected —
          </span>
          <Button
            data-testid="bulk-cancel-enrolment-btn"
            variant="outline"
            size="sm"
            disabled={busy}
            onClick={() => askConfirm("cancel")}
          >
            Cancel enrolment
          </Button>
          <Button
            data-testid="bulk-delete-btn"
            size="sm"
            disabled={busy}
            onClick={() => askConfirm("delete")}
            className="bg-red-700 hover:bg-red-800 text-white"
          >
            Delete selected
          </Button>
          <Button
            data-testid="bulk-delete-suppress-btn"
            size="sm"
            disabled={busy}
            onClick={() => askConfirm("deleteSuppress")}
            className="bg-red-900 hover:bg-red-950 text-white"
          >
            Delete + suppress
          </Button>
        </div>
      )}

      {confirm && (
        <div
          data-testid="bulk-confirm"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        >
          <div className="w-full max-w-md rounded bg-white p-5 shadow-lg">
            <div className="text-base font-semibold text-neutral-900">
              {ACTIONS[confirm.key].title(confirm.ids.length)}
            </div>
            <ul className="mt-3 space-y-1 text-sm text-neutral-700 list-disc pl-5">
              {ACTIONS[confirm.key].lines(confirm.ids.length).map((l) => (
                <li key={l}>{l}</li>
              ))}
            </ul>
            {confirm.related && (
              <div className="mt-3 rounded bg-neutral-50 px-3 py-2 text-xs text-neutral-600">
                Kept, not deleted: {confirm.related.quotes ?? 0} quote(s),{" "}
                {confirm.related.projects ?? 0} project(s),{" "}
                {confirm.related.marketing_sends ?? 0} sent email(s),{" "}
                {confirm.related.events ?? 0} activity record(s).
              </div>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <Button
                data-testid="bulk-confirm-cancel"
                variant="outline"
                onClick={() => setConfirm(null)}
                disabled={busy}
              >
                Keep them
              </Button>
              <Button
                data-testid="bulk-confirm-go"
                onClick={runAction}
                disabled={busy}
                className={
                  ACTIONS[confirm.key].danger
                    ? "bg-red-700 hover:bg-red-800 text-white"
                    : "bg-neutral-900 hover:bg-neutral-800 text-white"
                }
              >
                {busy ? "Working…" : ACTIONS[confirm.key].label}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="border border-neutral-200 rounded">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50">
            <tr>
              <th className="p-2 w-8">
                <Checkbox
                  data-testid="select-all"
                  checked={filtered.length > 0 && selected.size === filtered.length}
                  onCheckedChange={toggleAll}
                />
              </th>
              <th className="text-left p-2 text-xs uppercase tracking-wider text-neutral-500">Email</th>
              <th className="text-left p-2 text-xs uppercase tracking-wider text-neutral-500">Name</th>
              <th className="text-left p-2 text-xs uppercase tracking-wider text-neutral-500">Company</th>
              <th className="text-left p-2 text-xs uppercase tracking-wider text-neutral-500">Status</th>
              <th className="text-left p-2 text-xs uppercase tracking-wider text-neutral-500">Why not enrolled</th>
              <th className="text-left p-2 text-xs uppercase tracking-wider text-neutral-500">Entered</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(l => (
              <tr
                key={l.lead_id}
                className={
                  "border-t border-neutral-100 hover:bg-neutral-50 " +
                  (selected.has(l.lead_id) ? "bg-amber-50/40" : "")
                }
              >
                <td className="p-2">
                  <Checkbox
                    data-testid={`lead-checkbox-${l.lead_id}`}
                    checked={selected.has(l.lead_id)}
                    onCheckedChange={() => toggle(l.lead_id)}
                  />
                </td>
                <td className="p-2">
                  <Link
                    to={`/crm/leads/${l.lead_id}`}
                    className="text-neutral-900 hover:underline"
                  >
                    {l.contact_email}
                  </Link>
                </td>
                <td className="p-2 text-neutral-700">{l.contact_person || "—"}</td>
                <td className="p-2 text-neutral-700">{l.company_name || "—"}</td>
                <td className="p-2 text-neutral-500">{l.lead_status || "—"}</td>
                <td className="p-2">
                  <EnrolmentStatusPill status={l.enrolment_status} />
                </td>
                <td className="p-2 text-neutral-500">
                  {l.entry_date ? l.entry_date.slice(0, 10) : "—"}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={7} className="p-6 text-center text-neutral-400 text-sm">
                  No leads matching that search.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ─── Orphan enrolments tab ──────────────────────────────────────────


function OrphanEnrolmentsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [stopping, setStopping] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/marketing/orphan-enrollments?limit=500");
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggle = (id) => setSelected(s => {
    const n = new Set(s);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleAll = () => {
    if (selected.size === items.length) setSelected(new Set());
    else setSelected(new Set(items.map(x => x.enrollment_id)));
  };

  const stopSelected = async () => {
    if (selected.size === 0) {
      toast.warning("Select at least one enrolment");
      return;
    }
    const ok = window.confirm(
      `Stop ${selected.size} orphan enrolment${selected.size === 1 ? "" : "s"}? ` +
      `Rows are retained for audit; they just stop firing.`
    );
    if (!ok) return;
    setStopping(true);
    try {
      const r = await api.post("/admin/marketing/orphan-enrollments/stop", {
        enrollment_ids: [...selected],
      });
      toast.success(`Stopped ${r.data.stopped} of ${r.data.requested} orphan enrolments`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Stop failed");
    } finally {
      setStopping(false);
    }
  };

  if (loading) return <div className="text-neutral-500 text-sm">Loading…</div>;

  return (
    <div data-testid="orphans-pane">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-neutral-500">
          {items.length} orphan enrolment{items.length === 1 ? "" : "s"}
          {selected.size > 0 && ` · ${selected.size} selected`}
        </div>
        <Button
          data-testid="stop-orphans-btn"
          onClick={stopSelected}
          disabled={stopping || selected.size === 0}
          variant="destructive"
        >
          {stopping ? "Stopping…" : `Stop ${selected.size || ""}`.trim()}
        </Button>
      </div>

      <div className="border border-neutral-200 rounded">
        <table className="w-full text-sm">
          <thead className="bg-neutral-50">
            <tr>
              <th className="p-2 w-8">
                <Checkbox
                  data-testid="select-all-orphans"
                  checked={items.length > 0 && selected.size === items.length}
                  onCheckedChange={toggleAll}
                />
              </th>
              <th className="text-left p-2 text-xs uppercase tracking-wider text-neutral-500">Contact</th>
              <th className="text-left p-2 text-xs uppercase tracking-wider text-neutral-500">Workflow</th>
              <th className="text-left p-2 text-xs uppercase tracking-wider text-neutral-500">WF Status</th>
              <th className="text-left p-2 text-xs uppercase tracking-wider text-neutral-500">Enrolled</th>
              <th className="text-left p-2 text-xs uppercase tracking-wider text-neutral-500">Next action</th>
            </tr>
          </thead>
          <tbody>
            {items.map(o => (
              <tr
                key={o.enrollment_id}
                className={
                  "border-t border-neutral-100 hover:bg-neutral-50 " +
                  (selected.has(o.enrollment_id) ? "bg-amber-50/40" : "")
                }
              >
                <td className="p-2">
                  <Checkbox
                    data-testid={`orphan-checkbox-${o.enrollment_id}`}
                    checked={selected.has(o.enrollment_id)}
                    onCheckedChange={() => toggle(o.enrollment_id)}
                  />
                </td>
                <td className="p-2">
                  <div className="text-neutral-900">{o.contact_email || "—"}</div>
                  <div className="text-xs text-neutral-500">
                    {o.contact_name || o.company_name || ""}
                  </div>
                </td>
                <td className="p-2 text-neutral-700">{o.workflow_name}</td>
                <td className="p-2">
                  <span className="px-1.5 py-0.5 text-xs rounded bg-amber-100 text-amber-800">
                    {o.workflow_status}
                  </span>
                </td>
                <td className="p-2 text-neutral-500">
                  {o.enrolled_at ? o.enrolled_at.slice(0, 10) : "—"}
                </td>
                <td className="p-2 text-neutral-500">
                  {o.next_action_at ? o.next_action_at.slice(0, 16).replace("T", " ") : "—"}
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={6} className="p-6 text-center text-neutral-400 text-sm">
                  No orphan enrolments. 🟢
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}



// ─── "Why not enrolled" diagnosis pill ─────────────────────────────
//
// Renders the backend-supplied enrolment-status code as a colour-coded
// pill with the human-readable label visible inline and the full
// `detail` sentence on hover (native title attribute — no extra
// popover library needed). Colour mapping:
//
//   red    → unsubscribed, bounced, opted_out  (operator can't fix
//            without consent — re-enrolling would be a compliance risk)
//   amber  → suppressed                        (review individually)
//   blue   → completed                         (eligible — safe to
//            re-enrol)
//   grey   → never_synced, synced_never_enrolled, errored, cancelled,
//            paused, unknown                   (operational; safe to
//            enrol)

const PILL_STYLES = {
  unsubscribed:          "bg-red-50 text-red-800 border-red-200",
  bounced:               "bg-red-50 text-red-800 border-red-200",
  opted_out:             "bg-red-50 text-red-800 border-red-200",
  suppressed:            "bg-amber-50 text-amber-900 border-amber-200",
  completed:             "bg-sky-50 text-sky-800 border-sky-200",
  never_synced:          "bg-neutral-100 text-neutral-700 border-neutral-200",
  synced_never_enrolled: "bg-neutral-100 text-neutral-700 border-neutral-200",
  errored:               "bg-neutral-100 text-neutral-700 border-neutral-200",
  cancelled:             "bg-neutral-100 text-neutral-700 border-neutral-200",
  paused:                "bg-neutral-100 text-neutral-700 border-neutral-200",
  unknown:               "bg-neutral-100 text-neutral-500 border-neutral-200",
};

function EnrolmentStatusPill({ status }) {
  if (!status) {
    return <span className="text-neutral-400 text-xs">—</span>;
  }
  const css = PILL_STYLES[status.code] || PILL_STYLES.unknown;
  return (
    <span
      data-testid={`enrolment-status-${status.code}`}
      title={status.detail || ""}
      className={`inline-block px-2 py-0.5 text-xs rounded border cursor-help ${css}`}
    >
      {status.label}
    </span>
  );
}
